import {
  Box,
  CascadingFilterInput,
  CascadingFilterInputConfigItem,
  InputMessage,
  LinkAnchor,
  PrimaryButton,
} from '@wrisk/ui-components'
import { isNil, uniq } from 'lodash-es'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { toManufactureRangeKBA, toVariantKBA, VehicleKBA } from '../../../../../domain'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { WriskTrans } from '../../../trans'

export interface KBAVehicleSelectProps {
  value: VehicleKBA | null
  onChange: (value: VehicleKBA | null) => void
  onEmpty?: () => void
  results: VehicleKBA[]
  tKey: TKeyBuilder
}

export const VehicleSelect: FunctionComponent<KBAVehicleSelectProps> = ({
  value,
  results,
  onChange,
  onEmpty,
  tKey,
}) => {
  const { t } = useWriskTranslation()

  const [inputValue, setInputValue] = useState<VehicleKBA | null>(value)

  const handleSelect = useCallback((data: VehicleKBA) => {
    setInputValue(data ?? null)
  }, [])

  const handleConfirm = useCallback(() => {
    onChange(inputValue)
  }, [inputValue, onChange])

  const config = useMemo<Array<CascadingFilterInputConfigItem<VehicleKBA>>>(
    () => [
      {
        id: 'year',
        placeholder: t<string>(tKey('placeholders', 'year')),
        mapItemsToOptions: (items) => {
          const years = items.flatMap(toManufactureRangeKBA)

          return uniq(years)
            .toSorted()
            .map((year) => ({
              id: year.toString(),
              text: year.toString(),
            }))
        },
        matchItemToOption: (option) => (item) =>
          toManufactureRangeKBA(item).includes(Number(option.id)),
        matchOptionToItem: (item) => (option) =>
          toManufactureRangeKBA(item).includes(Number(option.id)),
      },
      {
        id: 'variant',
        placeholder: t<string>(tKey('placeholders', 'variant')),
        mapItemsToOptions: (items) => {
          const variants = items.map(toVariantKBA)

          return uniq(variants).map((variant) => ({
            id: variant,
            text: variant,
          }))
        },
        matchItemToOption: (option) => (item) => option.id === toVariantKBA(item),
        matchOptionToItem: (item) => (option) => option.id === toVariantKBA(item),
      },
    ],
    [t],
  )

  return (
    <Box
      width={1}
      display='flex'
      flexDirection='column'
      alignItems='flex-start'
      rowGap={4}
    >
      <InputMessage>{t(tKey('select'))}</InputMessage>
      <CascadingFilterInput<VehicleKBA>
        value={inputValue ?? undefined}
        items={results}
        onSelect={handleSelect}
        config={config}
        errorMessage={
          <WriskTrans
            t={t}
            i18nKey={tKey('errors', 'no_results')}
            components={{
              EmptyLink: <LinkAnchor variant='inline' onClick={onEmpty} />,
            }}
          />
        }
      />
      <PrimaryButton disabled={isNil(inputValue)} onClick={handleConfirm}>
        {t(tKey('actions', 'select'))}
      </PrimaryButton>
    </Box>
  )
}
